import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { serversApi } from '@/lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'

interface ResourcesTabProps {
  serverId: string
  serverName?: string
}

export function ResourcesTab({ serverId, serverName }: ResourcesTabProps) {
  const queryClient = useQueryClient()
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['serverResources', serverId],
    queryFn: async () => await serversApi.listResources(serverId),
    enabled: !!serverId,
  })

  const resources = Array.isArray((data as any)?.resources) ? (data as any).resources : []

  const [uri, setUri] = useState('')
  const [name, setName] = useState('')
  const [mimeType, setMimeType] = useState('text/plain')
  const [description, setDescription] = useState('')
  const [text, setText] = useState('')

  const addMutation = useMutation({
    mutationFn: async () => {
      if (!uri || !name) throw new Error('URI and Name are required')
      return await serversApi.addResource(serverId, {
        uri,
        name,
        description: description || undefined,
        mime_type: mimeType || undefined,
        text: text || undefined,
      })
    },
    onSuccess: () => {
      toast.success('Resource saved')
      queryClient.invalidateQueries({ queryKey: ['serverResources', serverId] })
      setUri('')
      setName('')
      setMimeType('text/plain')
      setDescription('')
      setText('')
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to save resource'),
  })

  const removeMutation = useMutation({
    mutationFn: async (targetUri: string) => await serversApi.removeResource(serverId, targetUri),
    onSuccess: () => {
      toast.success('Resource removed')
      queryClient.invalidateQueries({ queryKey: ['serverResources', serverId] })
    },
    onError: () => toast.error('Failed to remove resource'),
  })

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Resources</CardTitle>
          <CardDescription>List of MCP resources exposed by {serverName || 'this server'}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Loading resources...</p>
          ) : error ? (
            <div className="flex items-center justify-between">
              <p className="text-red-500">Failed to load resources</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
            </div>
          ) : resources.length === 0 ? (
            <p className="text-muted-foreground">No resources yet.</p>
          ) : (
            <div className="space-y-3">
              {resources.map((r: any, idx: number) => (
                <div key={idx} className="flex items-start justify-between border rounded p-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-xs">{r.mimeType || r.mime_type || 'text/plain'}</Badge>
                      <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{r.uri}</code>
                    </div>
                    <div className="text-sm font-medium">{r.name}</div>
                    {r.description && <div className="text-xs text-muted-foreground">{r.description}</div>}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => removeMutation.mutate(r.uri)}
                    disabled={removeMutation.isPending}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Add or Update Resource</CardTitle>
          <CardDescription>Creating with the same URI will update the resource</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm">URI</label>
              <Input value={uri} onChange={(e) => setUri(e.target.value)} placeholder="notes://welcome" />
            </div>
            <div>
              <label className="text-sm">Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Welcome Notes" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm">MIME Type</label>
              <Input value={mimeType} onChange={(e) => setMimeType(e.target.value)} placeholder="text/plain" />
            </div>
            <div>
              <label className="text-sm">Description</label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description" />
            </div>
          </div>
          <div>
            <label className="text-sm">Text Content (optional)</label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Hello!" rows={5} />
          </div>
          <div className="flex justify-end">
            <Button onClick={() => addMutation.mutate()} disabled={addMutation.isPending || !uri || !name}>
              Save Resource
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}


